import React, { useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import BonusPeriodsContext from '../../../../contexts/BonusPeriodsContext';
import Dropdown from '../../../../components/Inputs/Dropdown';
import DatasetDefinitionsContext from '../../../../contexts/DatasetDefinitionsContext';
import appNavItemTypeCheckers from '../../../../types/appNavItemTypeCheckers';
import flattenBonusSubPeriods from '../../../../contextProviders/BonusAppProvider/flattenBonusSubPeriods';
import getBonusPeriodDisplayLabel from 'formatters/getBonusPeriodDisplayLabel';
import styled from 'styled-components';

export const NavSectionLabelDiv = styled.div`
  padding-left: 8px;
  margin-bottom: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  span {
    color: #8e8e8e;
    font-family: Inter;
    font-weight: 500;
    font-size: 12px;
    font-style: normal;
    line-height: 14px; /* 107.692% */
    letter-spacing: -0.156px;
    text-transform: capitalize;
    overflow: hidden;
    white-space: nowrap;
  }
`;

// 'root' lists the real sign-off periods (used on the Approvals screen); 'sub'
// lists the individual sub-periods (weeks) when the programme uses them, and
// otherwise falls back to the real periods so ordinary programmes are unchanged.
export type BonusPeriodPickerVariant = 'root' | 'sub';

export const BonusPeriodPicker = ({
  variant = 'sub',
}: {
  variant?: BonusPeriodPickerVariant;
}) => {
  const {
    bonusPeriods,
    selectedBonusPeriod,
    setSelectedBonusPeriod,
    selectedSubPeriod,
    setSelectedSubPeriod,
  } = useContext(BonusPeriodsContext);

  // Flattened list of every sub-period across every period, so the one picker can
  // navigate directly to any week. Undefined when in root mode or when no period
  // carries sub-periods (the ordinary single-window case).
  const subPeriodOptions = useMemo(
    () =>
      variant === 'root' ? undefined : flattenBonusSubPeriods(bonusPeriods),
    [bonusPeriods, variant],
  );

  const options = useMemo((): DropdownOption[] => {
    if (subPeriodOptions) {
      return subPeriodOptions.map(({ period, subPeriod }) => ({
        key: `${period.id}:${subPeriod.startDate}`,
        label: getBonusPeriodDisplayLabel({
          label: subPeriod.label,
          status: period.status,
        }),
        isSelected:
          !!selectedSubPeriod &&
          selectedSubPeriod.startDate === subPeriod.startDate &&
          selectedBonusPeriod?.id === period.id,
        onSelected: () => {
          // Set the parent period first so id/status track the week, then the
          // week itself (both are URL-synced and re-resolved by BonusAppProvider).
          setSelectedBonusPeriod(period);
          setSelectedSubPeriod?.(subPeriod);
        },
      }));
    }

    return bonusPeriods.map((p) => ({
      key: p.id,
      label: getBonusPeriodDisplayLabel(p),
      isSelected: selectedBonusPeriod && selectedBonusPeriod.id === p.id,
      onSelected: () => {
        setSelectedBonusPeriod(p);
      },
    }));
  }, [
    subPeriodOptions,
    bonusPeriods,
    selectedBonusPeriod,
    selectedSubPeriod,
    setSelectedBonusPeriod,
    setSelectedSubPeriod,
  ]);

  return <Dropdown options={options} />;
};

/**
 * Period picker for a {@link V3.LegacyBonusApp}. Unlike the BonusApp
 * variant below there is no BonusProgram doc to label it with — periods
 * come from the global legacy BonusPeriodsProvider
 * (accounts/{id}/scoring/drivers/periods), so selection is shared across
 * every screen of the app, mirroring V1's workspace-level picker.
 */
export const LegacyBonusPeriodPickerNavItem = () => (
  <div style={{ margin: '0px 8px' }}>
    <BonusPeriodPicker />
  </div>
);

const BonusPeriodPickerNavItem = ({ app }: { app: V3.BonusApp }) => {
  const { bonusPrograms } = useContext(DatasetDefinitionsContext);
  const { pathname } = useLocation();

  const bonusProgramName = useMemo(() => {
    const p = bonusPrograms.find((bp) => bp.id === app.bonusProgramId);
    if (p) {
      return p.name;
    }
    return undefined;
  }, [app.bonusProgramId, bonusPrograms]);

  // The active screen's nav item is the last path segment. On the Approvals
  // screen the picker lists the real sign-off periods; everywhere else it lists
  // the sub-periods (weeks) when the programme uses them.
  const variant: BonusPeriodPickerVariant = useMemo(() => {
    const itemKey = pathname.split('/').filter(Boolean).pop();
    if (!itemKey) {
      return 'sub';
    }
    let selectedItem: V3.Navigation.Item | undefined;
    app.navigationSettings.sections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.key === itemKey) {
          selectedItem = item;
        }
      });
    });
    return selectedItem && appNavItemTypeCheckers.isApprovals(selectedItem)
      ? 'root'
      : 'sub';
  }, [app.navigationSettings.sections, pathname]);

  return (
    <div>
      {bonusProgramName && (
        <NavSectionLabelDiv>
          <span>{bonusProgramName}</span>
        </NavSectionLabelDiv>
      )}
      <div style={{ margin: '0px 8px' }}>
        <BonusPeriodPicker variant={variant} />
      </div>
    </div>
  );
};

export default BonusPeriodPickerNavItem;
